import { Warehouse } from '@mui/icons-material'
import { Stack, Typography } from '@mui/material'
import {
  ContainerPositionType,
  EquipmentDto,
  EquipmentType,
  JobDto,
  LocationDto,
} from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import {
  LongArrowRight,
  TrainIcon,
  TruckIcon,
  useMinimalsTheme,
  VesselIcon,
  YardIcon,
} from '@tom-ui/ui'

export interface Props {
  job: JobDto
  currentEquipment?: EquipmentDto
}
export const JobJourneyMinimalistic = ({ job, currentEquipment }: Props) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const isNotOnOrigin = job.cargoUnit?.currentLocation.type !== job.origin.type
  const isOnHandover = job.cargoUnit?.currentLocation.type === ContainerPositionType.Handover
  let destination = job.destination
  if (
    currentEquipment?.equipmentType !== EquipmentType.Tt &&
    job.linkedOutboundWorkInstruction?.destination
  )
    destination = job.linkedOutboundWorkInstruction.destination

  const originLocation =
    !job.cargoUnit?.displayName && job.suggestedEmptyContainer
      ? job.suggestedEmptyContainer?.currentLocation
      : job.origin

  return (
    <Stack
      flexDirection='row'
      alignItems='center'
      gap={theme.customSpacing.m}
      divider={<LongArrowRight fontSize='large' />}
    >
      <Location location={originLocation} finished={isNotOnOrigin} dataCy='job-card-origin' />

      {job.cargoUnit?.currentLocation.type === ContainerPositionType.Equipment &&
      job.cargoUnit.currentLocation.equipmentId !== currentEquipment?.id ? (
        <Location location={job.cargoUnit.currentLocation} />
      ) : (
        (isOnHandover || job.origin.type === ContainerPositionType.TrailerParkingLot) && (
          <Location
            location={{
              type: ContainerPositionType.Equipment,
              locationName: t('equipment', ' Equipment'),
              yardBlockName: '',
            }}
            finished={isOnHandover}
          />
        )
      )}

      <Location location={destination} dataCy='job-card-destination' />
    </Stack>
  )
}

interface LocationProps {
  location: LocationDto
  finished?: boolean
  dataCy?: string
}
const Location = ({ location, finished, dataCy }: LocationProps) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  let icon = <YardIcon fontSize='small' color={finished ? 'secondary' : 'inherit'} />

  switch (location.type) {
    case ContainerPositionType.Vessel:
    case ContainerPositionType.Berth:
      icon = <VesselIcon fontSize='small' color={finished ? 'secondary' : 'inherit'} />
      break
    case ContainerPositionType.Train:
      icon = <TrainIcon fontSize='small' color={finished ? 'secondary' : 'inherit'} />
      break
    case ContainerPositionType.Truck:
    case ContainerPositionType.Equipment:
    case ContainerPositionType.Handover:
      icon = <TruckIcon fontSize='small' color={finished ? 'secondary' : 'inherit'} />
      break
    case ContainerPositionType.Warehouse:
    case ContainerPositionType.Customs:
    case ContainerPositionType.Weights:
    case ContainerPositionType.Repair:
      icon = <Warehouse fontSize='small' color={finished ? 'secondary' : 'inherit'} />
      break
  }

  return (
    <Stack flexDirection='row' gap={theme.customSpacing.xs}>
      {icon}
      <Typography
        variant='body1'
        color={finished ? 'secondary' : 'inherit'}
        data-cy={dataCy}
        whiteSpace='nowrap'
      >
        {t(location.locationName, location.locationName)}
      </Typography>
    </Stack>
  )
}
